import {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Analytics from '../util/Analytics';
import Locale from '../util/Locale';
import './ReactImageScaler.scss';

const defaultFn = () => {};

function ReactImageScaler({
    width = 0,
    height = 0,
    src = null,
    imageSourceText = Locale.getText('NO_IMAGE_SOURCE'),
    scaleLabel = Locale.getText('SCALE'),
    scaleSizes = false,
    cancelLabel = Locale.getText('CANCEL'),
    resizeLabel = Locale.getText('RESIZE'),
    displayResolution,
    onScaleApply = defaultFn,
    onCancel = defaultFn
}) {
    const scaleValueRef = useRef();
    const scaleValueDisplay = useRef();
    const sourceRef = useRef();
    const [canvasWidth] = useState(width || window.innerWidth);
    const [canvasHeight] = useState(height || window.innerHeight);
    const [imageSource] = useState(src);
    const [overlay, setOverlay] = useState('');
    const [scale, setScale] = useState(1);
    const [image] = useState(new Image());

    const drawSourceCanvas = scaleArg => {
        const ctx = sourceRef.current.getContext('2d');
        const imageWidth = image.width * scaleArg;
        const imageHeight = image.height * scaleArg;
        sourceRef.current.width = imageWidth;
        sourceRef.current.height = imageHeight;
        ctx.drawImage(image, 0, 0, imageWidth, imageHeight);
    };

    const processImageData = async () => {
        const url = await sourceRef.current.toDataURL();
        return {image: url, width: sourceRef.current.width, height: sourceRef.current.height};
    };

    const redrawCanvas = scaleArg => {
        setScale(scaleArg);
        scaleValueRef.current.value = scaleArg;
        scaleValueDisplay.current.innerText = ` ${Math.floor(scaleArg * 100)}%`;
        drawSourceCanvas(scaleArg);
        const imageWidth = image.width * scaleArg;
        const imageHeight = image.height * scaleArg;
        if (displayResolution) {
            setOverlay(`${Math.floor(imageWidth)} X ${Math.floor(imageHeight)}`);
        }
    };

    useEffect(() => {
        if (src) {
            image.setAttribute('crossOrigin', ''); // TA1209572: must set crossOrigin BEFORE src otherwise Firefox gives an error: DOMException: The operation is insecure.
            image.src = imageSource;

            image.onload = () => {
                redrawCanvas(1);
            };
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cancelCrop = () => {
        if (onCancel && typeof onCancel === 'function') {
            onCancel();
        }
        return null;
    };

    const returnData = async () => {
        if (onScaleApply && typeof onScaleApply === 'function') {
            const data = await processImageData();
            onScaleApply(data);
            Analytics.trackEvent('Image Edit', 'Scale', 0, 'Image Scaled');
        }
        return null;
    };

    const scaleImage = event => {
        const newScale = event.target.value / 100;
        setScale(newScale);
        redrawCanvas(newScale);
    };

    const renderScaleSizes = () => {
        const scaleBy = newScale => {
            setScale(newScale);
            redrawCanvas(newScale);
        };
        return (
            <div className="control-scale-sizes">
                <nsemble-button primary={scale === 0.5 ? true : undefined} onClick={() => scaleBy(0.5)}>
                    0.5X
                </nsemble-button>
                <nsemble-button primary={scale === 1 ? true : undefined} onClick={() => scaleBy(1)}>
                    1X
                </nsemble-button>
                <nsemble-button primary={scale === 2 ? true : undefined} onClick={() => scaleBy(2)}>
                    2X
                </nsemble-button>
            </div>
        );
    };

    const renderNoSource = () => (
        <div className="scaler-no-source">
            <span>{imageSourceText}</span>
        </div>
    );

    const renderControls = () => (
        <div className="react-scaler-controls">
            <div className="control-segment">
                <span>{`${scaleLabel}: `}</span>
                <input type="number" ref={scaleValueRef} onChange={scaleImage} style={{display: 'none'}} />
                <span className="scale-percent" ref={scaleValueDisplay}>
                    100%
                </span>
            </div>
            <div className="control-segment">
                <input type="range" step={1} value={100 * scale} min="10" max={100} onChange={scaleImage} />
            </div>
            <div className="control-segment">
                {scaleSizes && renderScaleSizes()}
                <nsemble-button text onClick={cancelCrop}>
                    {cancelLabel}
                </nsemble-button>
                <nsemble-button text onClick={returnData}>
                    {resizeLabel}
                </nsemble-button>
            </div>
        </div>
    );

    const renderCanvas = () => (
        <div className="react-scaler">
            <div className="outer-canvas">
                <div className="inner-canvas">
                    <canvas ref={sourceRef} width={canvasWidth} height={canvasHeight} />
                </div>
            </div>
            {Boolean(overlay) && <div className="overlay">{overlay}</div>}
            {renderControls()}
        </div>
    );

    return imageSource ? renderCanvas() : renderNoSource();
}

ReactImageScaler.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    src: PropTypes.string,
    imageSourceText: PropTypes.string,
    scaleLabel: PropTypes.string,
    scaleSizes: PropTypes.bool,
    cancelLabel: PropTypes.string,
    resizeLabel: PropTypes.string,
    displayResolution: PropTypes.bool,
    onScaleApply: PropTypes.func,
    onCancel: PropTypes.func
};

export default ReactImageScaler;
